<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRoute } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { Loader2, Eye, Download } from 'lucide-vue-next'
  import { useReportThemesStore } from '@/stores/reportThemes/reportThemes'
  import { useMasterReportsStore } from '@/stores/masterReports/masterReports'
  import { Button } from '@/components/ui/button'
  import { Select } from "@/components/episto-ui/select";
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

  const route = useRoute()
  const themeToken = computed(() => route.params.themeToken as string)

  const store = useReportThemesStore()
  const { hasChanges, saving } = storeToRefs(store)
  const { saveEdits } = store

  const masterReportsStore = useMasterReportsStore()
  const { masterReports } = storeToRefs(masterReportsStore)

  const masterReportOptions = computed(() =>
    masterReports.value.map((t) => ({ value: t.token, label: t.name }))
  )

  const previewOpen = ref(false)
  const selectedMasterReportToken = ref('')
  const downloading = ref(false)
  const downloadError = ref<string | null>(null)

  onMounted(async () => {
    await masterReportsStore.fetchMasterReports()
    if (masterReports.value.length > 0) {
      selectedMasterReportToken.value = masterReports.value[0].token
    }
  })

  async function downloadPreview() {
    if (!selectedMasterReportToken.value) return
    downloading.value = true
    downloadError.value = null
    try {
      const csrfToken = localStorage.getItem('csrf_token') ?? ''
      const response = await fetch(
        `/api/report_themes/${themeToken.value}/preview_pptx?master_report_token=${selectedMasterReportToken.value}`,
        { headers: { 'X-CSRF-Token': csrfToken } },
      )
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `preview_report_theme.pptx`
      a.click()
      URL.revokeObjectURL(url)
      previewOpen.value = false
    } catch (e) {
      downloadError.value = e instanceof Error ? e.message : 'Download failed'
    } finally {
      downloading.value = false
    }
  }
</script>

<template>
  <div class="flex items-center gap-2">
    <!-- Download preview -->
    <Popover v-model:open="previewOpen">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          size="sm">
          <Eye class="w-4 h-4 mr-1.5" />
          Preview
        </Button>
      </PopoverTrigger>
      <PopoverContent
        class="w-64 p-3"
        align="end">
        <p class="text-xs font-medium mb-2">Master report</p>
        <Select
          :model-value="selectedMasterReportToken"
          :options="masterReportOptions"
          placeholder="Select..."
          @update:model-value="(v) => selectedMasterReportToken = String(v ?? '')" />
        <Button
          class="w-full mt-3"
          size="sm"
          :disabled="!selectedMasterReportToken || downloading"
          @click="downloadPreview">
          <Loader2
            v-if="downloading"
            class="w-4 h-4 mr-1.5 animate-spin" />
          <Download
            v-else
            class="w-4 h-4 mr-1.5" />
          Download
        </Button>
        <p
          v-if="downloadError"
          class="text-xs text-destructive mt-2">{{ downloadError }}</p>
      </PopoverContent>
    </Popover>

    <!-- Save -->
    <Button
      size="sm"
      :disabled="!hasChanges || saving"
      @click="saveEdits">
      <Loader2
        v-if="saving"
        class="w-4 h-4 mr-1.5 animate-spin" />
      Save
    </Button>
  </div>
</template>
